import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { usePageHeading } from './PageHeadingProvider';
import { useContext as useToastContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContext } from './ToastContext';
import { useAuth } from './AuthProvider';
import { useUserPermissionAndMenu } from './UserPermissionAndMenuProvider';
import {
    getMobileCustomersPaginated,
    approveCustomer,
    rejectCustomer,
    updateCustomer,
    createCustomer,
    getBranchLookup,
    getCustomerTypes,
    getCiCities,
    getDeactivateOptions,
    CustomerListDetail,
    BranchLookup,
    CustomerTypeLookup,
    CiCity,
    MobileCustomer,
    DeactivateOption,
} from '../services/approveMobileCustomerService';

interface MobileCustomerContextType {
    pageLoading: boolean;
    updateLoading: boolean;
    loadingMore: boolean;
    searchLoading: boolean;

    customers: CustomerListDetail[];
    branches: BranchLookup[];
    customerTypes: CustomerTypeLookup[];
    ciCities: CiCity[];
    deactivateOptions: DeactivateOption[];

    hasMore: boolean;
    totalCount: number;

    searchTerm: string;
    setSearchTerm: (term: string) => void;
    selectedBranch: string;
    setSelectedBranch: (branch: string) => void;
    selectedStatus: string;
    setSelectedStatus: (status: string) => void;
    selectedCustomerType: string;
    setSelectedCustomerType: (customerType: string) => void;
    hasBranchAll: boolean;
    branchLocked: boolean;

    handleSearch: () => Promise<void>;
    refreshCustomers: () => Promise<void>;
    loadMoreCustomers: () => Promise<void>;
    handleApprove: (customerNumber: string) => Promise<void>;
    handleReject: (customerNumber: string, payload: { deactivate_id: number; inactive_reason: string }) => Promise<void>;
    handleUpdate: (customerNumber: string, data: Partial<MobileCustomer>) => Promise<void>;
    handleCreate: (data: Partial<MobileCustomer>) => Promise<void>;
    addCityToState: (city: CiCity) => void;
}

const MobileCustomerContext = createContext<MobileCustomerContextType | undefined>(undefined);

export const MobileCustomerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { setPageHeading } = usePageHeading();
    const toast = useToastContext(ToastContext);
    const { t } = useTranslation();
    const { user } = useAuth();
    const { hasPermission } = useUserPermissionAndMenu();
    const hasBranchAll = hasPermission('BRANCH:ALL');
    const branchLocked = !hasBranchAll;

    const [pageLoading, setPageLoading] = useState(true);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);

    const [customers, setCustomers] = useState<CustomerListDetail[]>([]);
    const [branches, setBranches] = useState<BranchLookup[]>([]);
    const [customerTypes, setCustomerTypes] = useState<CustomerTypeLookup[]>([]);
    const [ciCities, setCiCities] = useState<CiCity[]>([]);
    const [deactivateOptions, setDeactivateOptions] = useState<DeactivateOption[]>([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBranch, setSelectedBranch] = useState(() => {
        if (!hasBranchAll && user?.active_branch_id) {
            return user.active_branch_id.toString();
        }
        return '';
    });
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedCustomerType, setSelectedCustomerType] = useState('');

    const refreshCustomers = useCallback(async () => {
        try {
            setPageLoading(true);
            const data = await getMobileCustomersPaginated({
                customer_number: searchTerm || undefined,
                branch_id: selectedBranch !== '' ? Number(selectedBranch) : undefined,
                status: selectedStatus === 'pending_mobile' ? 'pending_mobile' : (selectedStatus !== '' ? Number(selectedStatus) : undefined),
                customer_type_id: selectedCustomerType !== '' ? Number(selectedCustomerType) : undefined,
                page: 1,
                page_size: 10
            });
            setCustomers(data.items);
            setHasMore(data.has_more);
            setTotalCount(data.total);
            setCurrentPage(1);
        } catch (error: any) {
            toast?.showError(
                t('page.approve_mobile.error.title', { defaultValue: 'Error' }),
                error?.message ?? t('page.approve_mobile.failed_load_members', { defaultValue: 'Failed to load members' })
            );
            setCustomers([]);
            setHasMore(false);
            setTotalCount(0);
        } finally {
            setPageLoading(false);
        }
    }, [searchTerm, selectedBranch, selectedStatus, selectedCustomerType]);

    const handleSearch = useCallback(async () => {
        try {
            setSearchLoading(true);
            const statusValue = selectedStatus === 'pending_mobile' ? 'pending_mobile' : (selectedStatus !== '' ? Number(selectedStatus) : undefined);
            const data = await getMobileCustomersPaginated({
                customer_number: searchTerm || undefined,
                branch_id: selectedBranch !== '' ? Number(selectedBranch) : undefined,
                status: statusValue,
                customer_type_id: selectedCustomerType !== '' ? Number(selectedCustomerType) : undefined,
                page: 1,
                page_size: 10
            });
            setCustomers(data.items);
            setHasMore(data.has_more);
            setTotalCount(data.total);
            setCurrentPage(1);
        } catch (error: any) {
            toast?.showError(
                t('page.approve_mobile.error.title', { defaultValue: 'Error' }),
                error?.message ?? t('page.approve_mobile.failed_search_members', { defaultValue: 'Failed to search members' })
            );
            setCustomers([]);
            setHasMore(false);
            setTotalCount(0);
        } finally {
            setSearchLoading(false);
        }
    }, [searchTerm, selectedBranch, selectedStatus, selectedCustomerType, toast, t]);

    const loadMoreCustomers = useCallback(async () => {
        if (loadingMore || !hasMore) return;

        try {
            setLoadingMore(true);
            const nextPage = currentPage + 1;
            const statusValue = selectedStatus === 'pending_mobile' ? 'pending_mobile' : (selectedStatus !== '' ? Number(selectedStatus) : undefined);
            const data = await getMobileCustomersPaginated({
                customer_number: searchTerm || undefined,
                branch_id: selectedBranch !== '' ? Number(selectedBranch) : undefined,
                status: statusValue,
                customer_type_id: selectedCustomerType !== '' ? Number(selectedCustomerType) : undefined,
                page: nextPage,
                page_size: 10
            });

            setCustomers(prev => [...prev, ...data.items]);
            setHasMore(data.has_more);
            setCurrentPage(nextPage);
        } catch (error: any) {
            toast?.showError(
                t('page.approve_mobile.error.title', { defaultValue: 'Error' }),
                error?.message ?? t('page.approve_mobile.failed_load_more_members', { defaultValue: 'Failed to load more members' })
            );
        } finally {
            setLoadingMore(false);
        }
    }, [loadingMore, hasMore, currentPage, searchTerm, selectedBranch, selectedStatus, selectedCustomerType, toast, t]);

    useEffect(() => {
        setPageHeading(t('menu.members', { defaultValue: 'Members' }));
        const loadInitialData = async () => {
            try {
                const [branchData, customerTypeData, deactivateOptionsData, ciCitiesData] = await Promise.all([
                    getBranchLookup(),
                    getCustomerTypes(),
                    getDeactivateOptions(),
                    getCiCities()
                ]);
                setBranches(branchData);
                setCustomerTypes(customerTypeData);
                setDeactivateOptions(deactivateOptionsData);
                setCiCities(ciCitiesData);
                await refreshCustomers();
            } catch (error: any) {
                toast?.showError(
                    t('page.approve_mobile.error.title', { defaultValue: 'Error' }),
                    t('page.approve_mobile.failed_initialize', { defaultValue: 'Failed to initialize page data' })
                );
            }
        };

        loadInitialData();
    }, [setPageHeading, toast, t, refreshCustomers]);

    useEffect(() => {
        refreshCustomers();
    }, [selectedBranch, selectedStatus, selectedCustomerType, refreshCustomers]);

    const handleApprove = async (customerNumber: string) => {
        try {
            setUpdateLoading(true);
            await approveCustomer(customerNumber);
            toast?.showSuccess(
                t('page.approve_mobile.success.title', { defaultValue: 'Success' }),
                t('page.approve_mobile.approve_success', { defaultValue: 'Customer {{customerNumber}} approved successfully', customerNumber })
            );
            await refreshCustomers();
        } catch (error: any) {
            toast?.showError(
                t('page.approve_mobile.approval_failed', { defaultValue: 'Approval Failed' }),
                error?.message ?? t('page.approve_mobile.approval_failed_detail', { defaultValue: 'Failed to approve customer' })
            );
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleReject = async (customerNumber: string, payload: { deactivate_id: number; inactive_reason: string }) => {
        try {
            setUpdateLoading(true);
            await rejectCustomer(customerNumber, payload);
            toast?.showSuccess(
                t('page.approve_mobile.success.title', { defaultValue: 'Success' }),
                t('page.approve_mobile.reject_success', { defaultValue: 'Customer {{customerNumber}} rejected successfully', customerNumber })
            );
            await refreshCustomers();
        } catch (error: any) {
            toast?.showError(
                t('page.approve_mobile.reject_failed', { defaultValue: 'Reject Failed' }),
                error?.message ?? t('page.approve_mobile.reject_failed_detail', { defaultValue: 'Failed to reject customer' })
            );
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleUpdate = async (customerNumber: string, data: Partial<MobileCustomer>) => {
        try {
            setUpdateLoading(true);
            await updateCustomer(customerNumber, data);
            toast?.showSuccess(
                t('page.approve_mobile.success.title', { defaultValue: 'Success' }),
                t('page.approve_mobile.update_success', { defaultValue: 'Customer updated successfully' })
            );
            await refreshCustomers();
        } catch (error: any) {
            toast?.showError(
                t('page.approve_mobile.update_failed', { defaultValue: 'Update Failed' }),
                error?.message ?? t('page.approve_mobile.update_failed_detail', { defaultValue: 'Failed to update customer' })
            );
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleCreate = async (data: Partial<MobileCustomer>) => {
        try {
            setUpdateLoading(true);
            await createCustomer(data);
            toast?.showSuccess(
                t('page.approve_mobile.success.title', { defaultValue: 'Success' }),
                t('page.approve_mobile.creation_success', { defaultValue: 'New customer created successfully' })
            );
            await refreshCustomers();
        } catch (error: any) {
            toast?.showError(
                t('page.approve_mobile.creation_failed', { defaultValue: 'Creation Failed' }),
                error?.message ?? t('page.approve_mobile.creation_failed_detail', { defaultValue: 'Failed to create customer' })
            );
        } finally {
            setUpdateLoading(false);
        }
    };

    const addCityToState = (city: CiCity) => {
        setCiCities(prev => [...prev, city]);
    };

    const value: MobileCustomerContextType = {
        pageLoading,
        updateLoading,
        loadingMore,
        searchLoading,
        customers,
        branches,
        customerTypes,
        ciCities,
        deactivateOptions,
        hasMore,
        totalCount,
        searchTerm,
        setSearchTerm,
        selectedBranch,
        setSelectedBranch,
        selectedStatus,
        setSelectedStatus,
        selectedCustomerType,
        setSelectedCustomerType,
        hasBranchAll,
        branchLocked,
        handleSearch,
        refreshCustomers,
        loadMoreCustomers,
        handleApprove,
        handleReject,
        handleUpdate,
        handleCreate,
        addCityToState
    };

    return <MobileCustomerContext.Provider value={value}>{children}</MobileCustomerContext.Provider>;
};

export const useMobileCustomer = () => {
    const context = useContext(MobileCustomerContext);
    if (!context) {
        throw new Error('useMobileCustomer must be used within a MobileCustomerProvider');
    }
    return context;
};